import { Language, StudyMaterialText } from '@quizdb/common';

/** Names the layout rules a PDF is read with; part of its extraction id (PTX-D11, QZ-D83). */
export const EXTRACTOR_VERSION = 1;

/**
 * Names the OCR rules a page is read with; part of its extraction id (PHO-D7). 2: automatic page
 * segmentation; 1 read a photographed page as one block and kept nothing.
 */
export const OCR_VERSION = 2;

/** The longest edge a page is rendered to before OCR, in pixels (PHO-D3, PHO-D9). */
export const OCR_MAX_EDGE_PX = 3000;

/** Below this mean confidence (Tesseract's 0–100 scale), a paragraph block is dropped (PHO-D7). */
export const OCR_MIN_BLOCK_CONFIDENCE = 50;

/** How many Tesseract workers the OCR scheduler runs (PHO-D10). */
export const OCR_WORKERS = 2;

/** Below this many non-whitespace characters, a PDF page's text layer needs OCR (PHO-D12). */
export const OCR_PAGE_MAX_CHARS = 20;

/** One text run pdf.js reports, in PDF user-space units with `y` from the page's bottom. */
export interface TextItem {
  text: string;
  fontName: string;
  fontSize: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One PDF page's raw text runs, before layout rules turn them into kept text. */
export interface PageItems {
  page: number;
  items: TextItem[];
}

/** How far a PDF extraction has got, for a progress bar or similar. */
export interface ExtractionProgress {
  page: number;
  pdfPageCount: number;
}

/** What the extraction worker finds: the text, and the pages that need OCR, ascending (PHO-D12). */
export interface PdfTextResult {
  text: StudyMaterialText;
  ocrPages: number[];
}

/** Reads every page of `file`, reporting progress and honouring `signal` (PTX-D9). */
export type ExtractPdf = (
  file: Blob,
  onProgress: (progress: ExtractionProgress) => void,
  signal: AbortSignal
) => Promise<PdfTextResult>;

/** What the main thread posts to the extraction worker (PTX-D9). */
export interface ExtractionRequest {
  type: 'extract';
  data: ArrayBuffer;
}

/** What the extraction worker posts back, one `'progress'` message per page then one outcome. */
export type ExtractionMessage =
  | ({ type: 'progress' } & ExtractionProgress)
  | { type: 'done'; text: StudyMaterialText; ocrPages: number[] }
  | { type: 'failed'; code: 'PdfUnreadable' | 'unknown'; message: string };

/**
 * `MaterialPrepError`'s failure modes. The page shows `t(`error.${code}`)` (PDG-D9, PTX-D15).
 * The photo and OCR codes were added for photo jobs (PHO-D11).
 */
export type MaterialPrepErrorCode =
  | 'PdfUnreadable'
  | 'MaterialTooLarge'
  | 'PhotoUnsupported'
  | 'PhotoUnreadable'
  | 'TooManyPhotos'
  | 'OcrUnavailable'
  | 'unknown';

/** Thrown while hashing, checking or extracting a PDF before it is uploaded (PTX-D15). */
export class MaterialPrepError extends Error {
  readonly code: MaterialPrepErrorCode;

  constructor(code: MaterialPrepErrorCode) {
    super(code);
    this.name = 'MaterialPrepError';
    this.code = code;
  }
}

/**
 * Where `prepareMaterial` is in its work, for the step the new-job page shows. `'reading'` is
 * the OCR phase: `PdfMaterialSection` renders a phase as `t(`job.new.pdf.${phase}`)`.
 */
export type MaterialPrepPhase = 'hashing' | 'checking' | 'extracting' | 'reading' | 'uploading';

/**
 * A PDF whose extraction is ready on the API, either freshly extracted in this browser or
 * found there already (a cache hit). `text` is present only on a cache miss, so the preview can
 * read it locally instead of fetching a page back. `ocrPages` and `pageLanguage` are present
 * only when some pages were read by OCR (PHO-D1, PHO-D12).
 */
export interface PreparedMaterial {
  extractionId: string;
  name: string;
  pdfPageCount: number;
  pageChars: number[];
  text?: StudyMaterialText;
  ocrPages?: number[];
  pageLanguage?: Language;
}

/** How far OCR has got reading a PDF's scanned pages, counted in OCR pages, not PDF pages. */
export interface OcrProgress {
  done: number;
  total: number;
}

/** What the main thread posts to the render worker, which draws PDF pages for OCR (PHO-D9). */
export type RenderRequest = { type: 'open'; data: ArrayBuffer } | { type: 'render'; page: number };

/** What the render worker posts back, one `'image'` message per rendered page (PHO-D9). */
export type RenderMessage =
  | { type: 'opened'; pdfPageCount: number }
  | { type: 'image'; page: number; image: Blob }
  | { type: 'failed'; code: 'PdfUnreadable' | 'unknown'; message: string };

/** Renders one PDF page at a time to an image for OCR, on an OffscreenCanvas (PHO-D9). */
export interface PdfRenderer {
  render(page: number): Promise<Blob>;
  close(): void;
}

/** Opens `file` for rendering, honouring `signal` (PHO-D9). */
export type OpenPdfRenderer = (file: Blob, signal: AbortSignal) => Promise<PdfRenderer>;
